import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { UserService } from '@services/user.service';
import { notificationCenterItem } from '@shared/models/notificationcenter';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

export type NotificationPlacement = 'top-left' | 'top-right';

export interface NotifyOptions {
	placement?: NotificationPlacement;
	impaired?: boolean;
}

@Injectable({
	providedIn: 'root',
})
export class NotificationService {
	private allNotifications: notificationCenterItem[] = [];
	private floatingNotifications: notificationCenterItem[] = [];
	private unreadCount: number = 0;
	private alert: boolean = false;
	private centerIsVisible: boolean = false;

	private floatingMessages: Subject<notificationCenterItem[]> = new Subject<notificationCenterItem[]>();
	public floatingMessages$: Observable<notificationCenterItem[]> = this.floatingMessages.asObservable();

	private unreadSubject: Subject<number> = new Subject<number>();
	public unreadCount$: Observable<number> = this.unreadSubject.asObservable();

	private allMessages: Subject<notificationCenterItem[]> = new BehaviorSubject<notificationCenterItem[]>([]);
	public allMessages$: Observable<notificationCenterItem[]> = this.allMessages.asObservable();

	private notificationCenterVisible: Subject<boolean> = new Subject<boolean>();
	public notificationCenterVisible$: Observable<boolean> = this.notificationCenterVisible.asObservable();

	private errorNotifications: Subject<boolean> = new Subject<boolean>();
	public errorNotifications$: Observable<boolean> = this.errorNotifications.asObservable();

	constructor(
		private translateService: TranslateService,
		private userService: UserService,
	) {
		// this.allMessages.subscribe(m => console.log('CJS m', m));
		this.userService.accountChanged$.subscribe(() => this.resetAll());
	}

	addNotification(n: notificationCenterItem): void {
		this.allNotifications.unshift(n);
		this.allMessages.next(this.allNotifications);
		this.getFloatingNotifications();
	}

	// The floating notifications contain any notification younger than 5 seconds.
	// I should probably add an "isRead" property, so that clicked-away notifications do not "float" again if another
	// notification is generated within 5 seconds after the clicked-away one.
	getFloatingNotifications(maxAge: number = 5): void {
		this.floatingNotifications = this.allNotifications.filter((n) => {
			const age = (Date.now() - n.date.valueOf()) / 1000;
			return age <= maxAge;
		});
		this.floatingMessages.next(this.floatingNotifications);
	}

	removeFloatingNotification(index: number = undefined): void {
		if (index === undefined) return;
		this.floatingNotifications.splice(index, 1);
		// this.getFloatingNotifications();
	}

	removeCenterNotification(index: number = undefined): void {
		if (index === undefined) return;
		this.allNotifications.splice(index, 1);
	}

	addUnread() {
		// if (this.centerIsVisible) return;
		this.unreadCount++;
		this.unreadSubject.next(this.unreadCount);
	}

	resetUnread() {
		this.unreadCount = 0;
		this.unreadSubject.next(this.unreadCount);
	}

	getNotificationCount() {
		return this.allNotifications.length;
	}

	resetAll() {
		this.resetUnread();
		this.allNotifications = [];
		this.allMessages.next(this.allNotifications);
		this.floatingNotifications = [];
		this.floatingMessages.next(this.floatingNotifications);
		this.setAlert(false);
	}

	// Set to true if message of type error is present among the unread notifications.
	// Should affect the background color of the number/count next to the bell icon in our topbar.
	setAlert(hasAlert: boolean = true) {
		this.alert = hasAlert;
		this.errorNotifications.next(this.alert);
	}

	toggleNotificationCenter() {
		if (this.centerIsVisible) {
			this.resetUnread();
			this.setAlert(false);
		}
		this.centerIsVisible = !this.centerIsVisible;
		this.notificationCenterVisible.next(this.centerIsVisible);
	}

	// A shorthand function to quickly generate a notification
	notify(headingKeyAndVars: any[], contentKeyAndVars: any[], type: 'error' | 'success' | 'neutral', options?: NotifyOptions) {
		const placement: NotificationPlacement = options?.placement === 'top-left' ? 'top-left' : 'top-right';
		const impaired = options?.impaired === true;

		// Timeout of zero to make sure that the translations are loaded
		setTimeout(() => {
			const heading = this.translateService.instant(headingKeyAndVars.shift(), headingKeyAndVars.shift());
			const text = contentKeyAndVars.length ? this.translateService.instant(contentKeyAndVars.shift(), contentKeyAndVars.shift()) : '';
			const date = new Date();
			const account = this.userService.account;
			this.addNotification({ heading, text, type, date, account, placement, impaired });
		}, 0);
	}
}
